import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { useApp } from '@/contexts/AppContext';
import { useApiError } from '@/hooks/useApiError';
import { registerSchema, RegisterFormData } from '@/types/forms';
import { AuthService } from '../services/auth.service';
import { AuthLayout } from '../components/AuthLayout';
import { RegisterBrandingPanel } from '../components/RegisterBrandingPanel';
import { Icon } from '@/shared/icons';

/** Cabeçalho de seção numerada (Empresa / Pessoais / Endereço / Acesso). */
const SectionHeader: React.FC<{ n: number; title: string }> = ({ n, title }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span
      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-none text-white"
      style={{ backgroundColor: 'var(--theme-primary)' }}
    >
      {n}
    </span>
    <span className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>
      {title}
    </span>
  </div>
);

export const Register: React.FC = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const { app } = useApp();
  const { handleApiError } = useApiError();
  const navigate = useNavigate();

  const primaryColor = app?.themeOverrides?.colors?.primary || '#8b5cf6';
  const brandName = app?.name || 'TROIA';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onTouched',
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const response = await AuthService.register({
        name: data.companyName,
        email: data.companyEmail,
        phone: data.companyPhone,
        address: {
          street: data.street,
          number: data.number,
          complement: data.complement,
          neighborhood: data.neighborhood,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode.replace(/\D/g, ''),
          country: 'BR',
        },
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
        },
      });

      localStorage.setItem('auth_token', response.token);
      if (response.refreshToken) {
        localStorage.setItem('refresh_token', response.refreshToken);
      }

      toast.success('Conta criada com sucesso! Faça login para continuar.');
      navigate('/login', { state: { email: data.email } });
    } catch (error) {
      handleApiError(error, setError);
    }
  };

  return (
    <AuthLayout>
      <div
        className="flex flex-col min-[860px]:flex-row min-[860px]:h-screen min-[860px]:overflow-hidden"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        {/* Painel de marca — fixo à esquerda (não rola); some no mobile */}
        <RegisterBrandingPanel app={app} primaryColor={primaryColor} />

        {/* Coluna do formulário — rola internamente no desktop */}
        <main className="flex-1 min-[860px]:w-1/2 min-[860px]:overflow-y-auto">
          <div className="min-h-full flex items-center justify-center px-5 py-11 min-[860px]:py-16">
            <div className="w-full max-w-[440px]">
              {/* Logo compacto (só mobile) */}
              <div className="min-[860px]:hidden flex items-center gap-2.5 mb-7">
                {app?.logo ? (
                  <img src={app.logo} alt={brandName} className="h-9 w-auto" />
                ) : (
                  <span
                    className="w-9 h-9 rounded-[9px] flex items-center justify-center text-lg font-black text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {brandName.charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="text-lg font-extrabold tracking-tight" style={{ color: 'var(--theme-text)' }}>
                  {brandName}
                </span>
              </div>

              {/* Título */}
              <h1 className="text-2xl font-bold tracking-tight mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Criar sua conta
              </h1>
              <p className="text-sm mb-7" style={{ color: 'var(--theme-muted-text)' }}>
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="font-semibold hover:underline"
                  style={{ color: 'var(--theme-primary)' }}
                  data-testid="register-login-link"
                >
                  Faça login
                </Link>
              </p>

              {/* Formulário — página única, 4 seções */}
              <form onSubmit={handleSubmit(onSubmit)} data-testid="register-form" className="space-y-7">
                {/* Seção 1 — Empresa */}
                <div>
                  <SectionHeader n={1} title="Empresa" />
                  <div className="space-y-4">
                    <FormField label="Nome da empresa" error={errors.companyName?.message} required>
                      <Input
                        {...register('companyName')}
                        type="text"
                        placeholder="Nome da sua empresa"
                        icon="briefcase"
                        error={errors.companyName?.message}
                        data-testid="register-companyName-input"
                      />
                    </FormField>

                    <FormField label="E-mail da empresa" error={errors.companyEmail?.message} required>
                      <Input
                        {...register('companyEmail')}
                        type="email"
                        placeholder="contato@empresa.com"
                        icon="mail"
                        error={errors.companyEmail?.message}
                        data-testid="register-companyEmail-input"
                      />
                    </FormField>

                    <FormField label="Telefone (opcional)" error={errors.companyPhone?.message}>
                      <Input
                        {...register('companyPhone')}
                        type="tel"
                        placeholder="(11) 99999-9999"
                        icon="phone"
                        error={errors.companyPhone?.message}
                        data-testid="register-companyPhone-input"
                      />
                    </FormField>
                  </div>
                </div>

                {/* Seção 2 — Pessoais */}
                <div>
                  <SectionHeader n={2} title="Pessoais" />
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Nome" error={errors.firstName?.message} required>
                        <Input
                          {...register('firstName')}
                          type="text"
                          placeholder="Seu nome"
                          icon="user"
                          error={errors.firstName?.message}
                          data-testid="register-firstName-input"
                        />
                      </FormField>

                      <FormField label="Sobrenome" error={errors.lastName?.message} required>
                        <Input
                          {...register('lastName')}
                          type="text"
                          placeholder="Seu sobrenome"
                          icon="user"
                          error={errors.lastName?.message}
                          data-testid="register-lastName-input"
                        />
                      </FormField>
                    </div>

                    <FormField label="E-mail" error={errors.email?.message} required>
                      <Input
                        {...register('email')}
                        type="email"
                        placeholder="seu@email.com"
                        icon="mail"
                        error={errors.email?.message}
                        data-testid="register-email-input"
                      />
                    </FormField>
                  </div>
                </div>

                {/* Seção 3 — Endereço */}
                <div>
                  <SectionHeader n={3} title="Endereço" />
                  <div className="space-y-4">
                    <FormField label="CEP" error={errors.zipCode?.message} required>
                      <Input
                        {...register('zipCode')}
                        type="text"
                        placeholder="00000-000"
                        icon="map-pin"
                        error={errors.zipCode?.message}
                        data-testid="register-zipCode-input"
                      />
                    </FormField>

                    <FormField label="Rua" error={errors.street?.message} required>
                      <Input
                        {...register('street')}
                        type="text"
                        placeholder="Nome da rua"
                        error={errors.street?.message}
                        data-testid="register-street-input"
                      />
                    </FormField>

                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Número" error={errors.number?.message} required>
                        <Input
                          {...register('number')}
                          type="text"
                          placeholder="123"
                          error={errors.number?.message}
                          data-testid="register-number-input"
                        />
                      </FormField>

                      <FormField label="Complemento" error={errors.complement?.message}>
                        <Input
                          {...register('complement')}
                          type="text"
                          placeholder="Apto, Sala..."
                          error={errors.complement?.message}
                          data-testid="register-complement-input"
                        />
                      </FormField>
                    </div>

                    <FormField label="Bairro" error={errors.neighborhood?.message} required>
                      <Input
                        {...register('neighborhood')}
                        type="text"
                        placeholder="Nome do bairro"
                        error={errors.neighborhood?.message}
                        data-testid="register-neighborhood-input"
                      />
                    </FormField>

                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Cidade" error={errors.city?.message} required>
                        <Input
                          {...register('city')}
                          type="text"
                          placeholder="Nome da cidade"
                          error={errors.city?.message}
                          data-testid="register-city-input"
                        />
                      </FormField>

                      <FormField label="Estado" error={errors.state?.message} required>
                        <Input
                          {...register('state')}
                          type="text"
                          placeholder="SP"
                          maxLength={2}
                          error={errors.state?.message}
                          data-testid="register-state-input"
                        />
                      </FormField>
                    </div>
                  </div>
                </div>

                {/* Seção 4 — Acesso */}
                <div>
                  <SectionHeader n={4} title="Acesso" />
                  <div className="space-y-4">
                    <FormField label="Senha" error={errors.password?.message} required>
                      <div className="relative">
                        <Input
                          {...register('password')}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Mínimo 8 caracteres"
                          icon="lock"
                          className="pr-9"
                          error={errors.password?.message}
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ color: 'var(--theme-muted-text)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-text)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          data-testid="register-password-toggle"
                        >
                          <Icon name={showPassword ? 'eye-off' : 'eye'} className="h-4 w-4" />
                        </button>
                      </div>
                    </FormField>

                    <FormField label="Confirmar senha" error={errors.confirmPassword?.message} required>
                      <div className="relative">
                        <Input
                          {...register('confirmPassword')}
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Repita sua senha"
                          icon="lock"
                          className="pr-9"
                          error={errors.confirmPassword?.message}
                          data-testid="register-confirmPassword-input"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{ color: 'var(--theme-muted-text)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-text)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
                          aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          data-testid="register-confirmPassword-toggle"
                        >
                          <Icon name={showConfirmPassword ? 'eye-off' : 'eye'} className="h-4 w-4" />
                        </button>
                      </div>
                    </FormField>

                    {/* Termos */}
                    <div>
                      <div className="flex items-start gap-2.5">
                        <input
                          {...register('acceptTerms')}
                          id="acceptTerms"
                          type="checkbox"
                          className="h-[18px] w-[18px] rounded mt-0.5 flex-none"
                          style={{ accentColor: 'var(--theme-primary)' }}
                          data-testid="register-acceptTerms-checkbox"
                        />
                        <label htmlFor="acceptTerms" className="text-[13px] leading-5" style={{ color: 'var(--theme-text-secondary)' }}>
                          Eu aceito os{' '}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold hover:underline"
                            style={{ color: 'var(--theme-primary)' }}
                          >
                            termos de uso
                          </a>{' '}
                          e{' '}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold hover:underline"
                            style={{ color: 'var(--theme-primary)' }}
                          >
                            política de privacidade
                          </a>
                        </label>
                      </div>
                      {errors.acceptTerms && (
                        <p className="mt-1.5 text-sm" style={{ color: 'var(--theme-error-color)' }} role="alert">
                          {errors.acceptTerms.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  fullWidth
                  loading={isSubmitting}
                  style={{ height: 47, fontSize: 15 }}
                  data-testid="register-submit"
                >
                  Criar conta
                </Button>
              </form>

              {/* Legal */}
              <p className="mt-6 text-center text-[12.5px] leading-relaxed" style={{ color: 'var(--theme-muted-text)' }}>
                Ao criar sua conta, você concorda com nossos{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--theme-primary)' }}>
                  Termos de Uso
                </a>{' '}
                e{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--theme-primary)' }}>
                  Política de Privacidade
                </a>
              </p>
            </div>
          </div>
        </main>
      </div>
    </AuthLayout>
  );
};
